import React, { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAtom, useSetAtom } from 'jotai'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, Dialog, Input2FA } from '@node-space/ui-components'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { useGetMassFiatPayoutsDetailsQuery } from 'hooks/queries/MassPayouts/useGetMassFiatPayoutsDetails'
import { Currencies, WalletTypes } from 'types/types'
import {
  authRequestAtom,
  isConfirmBatchModalVisibleAtom,
  walletTypeAtom,
} from '../atoms/massPayoutAtoms'
import { MassPayoutConfirmationSchema } from '../schemas/massPayoutConfirmationSchema'
import {
  BatchSummaryInformation,
  MassPayoutConfirmationPayload,
  MassPayoutPostAuthRequest,
} from '../types'
import { MassPayoutConfirmationSummary } from './MassPayoutConfirmationSummary'

type MassPayoutConfirmationModalProps = {
  onContinue: (password?: string) => void
  onClose: () => void
  batchInformation: BatchSummaryInformation
  isLoading: boolean
  error: boolean
  twoFaRequired: boolean
}

export const MassPayoutConfirmationModal = ({
  onContinue,
  onClose,
  batchInformation,
  isLoading,
  error,
  twoFaRequired,
}: MassPayoutConfirmationModalProps) => {
  const { t } = useTranslation()

  const [authRequest, setAuthRequest] = useAtom<MassPayoutPostAuthRequest>(authRequestAtom)
  const setIsConfirmBatchModalVisible = useSetAtom(isConfirmBatchModalVisibleAtom)
  const [walletType] = useAtom(walletTypeAtom)
  const isFiat = walletType === WalletTypes.FIAT

  const formSchema = MassPayoutConfirmationSchema()
  const { control, register, setValue } = useForm<MassPayoutConfirmationPayload>({
    resolver: yupResolver(formSchema),
  })
  const password = useWatch({
    control,
    name: 'password',
    defaultValue: '',
  })

  const batchId = batchInformation?.reference

  const { data: fiatBatchDetails } = useGetMassFiatPayoutsDetailsQuery(
    batchId || '',
    isFiat && !!batchId
  )

  useEffect(() => {
    if (password?.length === TWO_FA_REQUIRED_LENGTH) {
      setAuthRequest({ ...authRequest, tokenResponse: password })
      onContinue(password)
    }
  }, [password])

  const isVopDisclaimerRequired = isFiat && fiatBatchDetails?.currency === Currencies.EUR

  return (
    <Dialog
      title={t('massPayouts.submitPayments')}
      open
      onClose={onClose}
      type="utility"
      {...(!twoFaRequired && {
        ActionButtons: [
          <Button key="submit" onClick={() => onContinue()}>
            {t('submit')}
          </Button>,
          <Button
            key="cancel"
            variant="secondary"
            onClick={() => setIsConfirmBatchModalVisible(false)}
          >
            {t('cancel')}
          </Button>,
        ],
      })}
    >
      <Box flex flexCol paddingT={16}>
        <MassPayoutConfirmationSummary batchInformation={batchInformation} />

        <Box paddingT={16}>
          {isVopDisclaimerRequired && (
            <Callout variant="warning">{t('fiatMassPayoutsVOPDisclaimer')}</Callout>
          )}
        </Box>

        {twoFaRequired && (
          <Box padding={24}>
            <Input2FA
              {...register('password')}
              label={t('authCodeConfirm')}
              onChange={value => setValue('password', value)}
              testId="input-2fa"
              error={error && t('incorrectOtpCode')}
              loading={isLoading}
            />
          </Box>
        )}
      </Box>
    </Dialog>
  )
}
